import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { parseLocalDate } from '../../../utils/date';

dayjs.extend(relativeTime);

// Event type formatting
export const formatEventType = (eventType: string): string => {
  const typeMap: Record<string, string> = {
    // Lease events
    lease_execution: 'Lease Execution',
    lease_commencement: 'Lease Commencement',
    rent_commencement: 'Rent Commencement',
    lease_expiration: 'Lease Expiration',
    renewal_notice_deadline: 'Renewal Notice',
    termination_option_deadline: 'Termination Option',
    expansion_option_deadline: 'Expansion Option',
    contraction_option_deadline: 'Contraction Option',
    rofr_rofo_deadline: 'ROFR/ROFO',
    rent_escalation_first: 'Rent Escalation',
    cam_reconciliation_due: 'CAM Reconciliation',
    security_deposit_return: 'Security Deposit',
    estoppel_due: 'Estoppel Certificate',
    snda_timing: 'SNDA Timing',
    insurance_renewal: 'Insurance Renewal',
    landlord_delivery: 'Landlord Delivery',
    tenant_work_deadline: 'Tenant Work',
    retail_opening_deadline: 'Store Opening',
    cotenancy_cure_deadline: 'Co-tenancy Cure',
    
    // PSA events
    psa_execution: 'PSA Execution',
    earnest_money_due: 'Earnest Money',
    due_diligence_end: 'Due Diligence',
    title_objection_due: 'Title Objection',
    seller_cure_deadline: 'Seller Cure',
    financing_contingency: 'Financing Contingency',
    appraisal_contingency: 'Appraisal Contingency',
    estoppel_delivery_psa: 'Estoppel Delivery',
    closing_date: 'Closing Date',
    outside_closing_date: 'Outside Closing',
    exchange_45_day: '1031 Exchange (45-day)',
    exchange_180_day: '1031 Exchange (180-day)',
  };

  return typeMap[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

// Get urgency level based on date and status
export const getEventUrgency = (eventDate: string, status: string): string => {
  if (status === 'completed') return 'normal';
  
  const now = dayjs();
  const event = parseLocalDate(eventDate);
  const daysUntil = event.diff(now, 'day');

  if (daysUntil < 0) return 'critical'; // Past due
  if (daysUntil <= 7) return 'warning'; // Within a week
  if (daysUntil <= 30) return 'upcoming'; // Within a month
  return 'normal';
};

// Get status badge styling
export const getEventStatusBadge = (status: string): React.ReactElement => {
  const statusConfig: Record<string, { label: string; className: string }> = {
    active: { label: 'Active', className: 'badge-primary' },
    upcoming: { label: 'Upcoming', className: 'badge-info' },
    past_due: { label: 'Past Due', className: 'badge-danger' },
    completed: { label: 'Completed', className: 'badge-success' },
  };

  const config = statusConfig[status] || { label: status, className: 'badge-secondary' };

  return React.createElement(
    'span',
    { className: `badge ${config.className}` },
    config.label
  );
};

// Format date for display
export const formatEventDate = (date: string): string => {
  return parseLocalDate(date).format('MMM D, YYYY');
};

// Get relative time description
export const getRelativeTime = (date: string): string => {
  const eventDate = parseLocalDate(date).startOf('day');
  const now = dayjs().startOf('day');
  const diffDays = eventDate.diff(now, 'days');

  if (diffDays === 0) {
    return 'today';
  } else if (diffDays === 1) {
    return 'in 1d';
  } else if (diffDays > 1) {
    return `in ${diffDays}d`;
  } else if (diffDays === -1) {
    return '1d ago';
  } else {
    return `${Math.abs(diffDays)}d ago`;
  }
};

// Extract primary actor from context
export const extractPrimaryActor = (actorContext: any): string => {
  if (!actorContext) return '';
  
  if (typeof actorContext === 'string') {
    try {
      actorContext = JSON.parse(actorContext);
    } catch {
      return '';
    }
  }
  
  return actorContext.primary_actor || '';
};

// Extract required action from context
export const extractRequiredAction = (actionContext: any): string => {
  if (!actionContext) return '';
  
  if (typeof actionContext === 'string') {
    try {
      actionContext = JSON.parse(actionContext);
    } catch {
      return '';
    }
  }
  
  return actionContext.required_action || '';
};

// Format financial amount
export const formatFinancialAmount = (amount: number | string): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// Get event category for grouping
export const getEventCategory = (eventType: string): string => {
  const leaseEvents = [
    'lease_execution', 'lease_commencement', 'rent_commencement', 'lease_expiration',
    'renewal_notice_deadline', 'termination_option_deadline', 'expansion_option_deadline',
    'contraction_option_deadline', 'rofr_rofo_deadline', 'rent_escalation_first',
    'cam_reconciliation_due', 'security_deposit_return', 'estoppel_due', 'snda_timing',
    'insurance_renewal', 'landlord_delivery', 'tenant_work_deadline', 'retail_opening_deadline',
    'cotenancy_cure_deadline'
  ];

  const psaEvents = [
    'psa_execution', 'earnest_money_due', 'due_diligence_end', 'title_objection_due',
    'seller_cure_deadline', 'financing_contingency', 'appraisal_contingency',
    'estoppel_delivery_psa', 'closing_date', 'outside_closing_date',
    'exchange_45_day', 'exchange_180_day'
  ];

  if (leaseEvents.includes(eventType)) return 'Lease';
  if (psaEvents.includes(eventType)) return 'Purchase & Sale';
  return 'Other';
};